<template>
    <SideDrawer :open="open" title="Сбросить пароль" :subtitle="user?.email || ''" :busy="isSaving" @close="emit('close')">
        <form id="reset-password-form" class="reset-form" novalidate @submit.prevent="submit">
            <PasswordField
                input-id="reset-user-password"
                v-model="password"
                :error="fieldError"
                :disabled="isSaving"
                :locked="isDone"
            >
                <template #hint>
                    {{ isDone
                        ? 'Пароль установлен. Скопируйте его и передайте пользователю — после закрытия окна он больше не отобразится. Текущие входы этого пользователя завершены.'
                        : `Не короче ${MIN_PASSWORD_LENGTH} символов. Скопируйте пароль и передайте его лично: письмо не отправляется. После сброса текущие входы этого пользователя завершатся.` }}
                </template>
            </PasswordField>
            <p v-if="generalError" class="form-error" role="alert">{{ generalError }}</p>
        </form>

        <template #footer>
            <button type="button" class="btn-secondary" :disabled="isSaving" @click="emit('close')">
                {{ isDone ? 'Закрыть' : 'Отмена' }}
            </button>
            <button
                v-if="!isDone"
                type="submit"
                form="reset-password-form"
                class="btn-primary"
                :disabled="!canSubmit || isSaving"
            >
                {{ isSaving ? 'Сохранение…' : 'Сбросить пароль' }}
            </button>
        </template>
    </SideDrawer>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import axios from 'axios';
import SideDrawer from '../../ui/SideDrawer.vue';
import PasswordField from './PasswordField.vue';
import { MIN_PASSWORD_LENGTH } from '../../../constants/users';
import { extractErrorMessage, extractFieldErrors } from '../../../utils/httpErrors';

const props = defineProps({
    open: { type: Boolean, required: true },
    user: { type: Object, default: null },
});

const emit = defineEmits(['close', 'saved']);

const password = ref('');
const fieldError = ref('');
const generalError = ref('');
const isSaving = ref(false);
const isDone = ref(false);

const canSubmit = computed(() => password.value.length >= MIN_PASSWORD_LENGTH);

const resetState = () => {
    password.value = '';
    fieldError.value = '';
    generalError.value = '';
    isSaving.value = false;
    isDone.value = false;
};

watch(() => props.open, (isOpen) => {
    if (isOpen) resetState();
});

const submit = async () => {
    if (!canSubmit.value || isSaving.value || isDone.value || !props.user) return;

    isSaving.value = true;
    fieldError.value = '';
    generalError.value = '';

    try {
        const { data } = await axios.post(`/administration/users/${props.user.id}/password`, {
            password: password.value,
        });
        isDone.value = true;
        emit('saved', { message: data.message || 'Пароль сброшен' });
    } catch (error) {
        const errors = extractFieldErrors(error);
        fieldError.value = errors.password || '';
        if (!fieldError.value) generalError.value = errors.user || extractErrorMessage(error);
    } finally {
        isSaving.value = false;
    }
};
</script>

<style scoped lang="scss">
@use '../../../../scss/pages/admin/admin-tokens' as *;
@use '../../../../scss/pages/admin/admin-form';

@include admin-form.fields;

.reset-form {
    display: flex;
    flex-direction: column;
    gap: $spacing-xl;
}
</style>
